import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * "Am I taking work right now?" — stated, not guessed.
 *
 * Until this existed, a creator's only way into a brand's field of view was to
 * happen to match a filter someone was already typing. This is the other half:
 * a status, a badge, and the subject areas the account wants, all of which the
 * discovery ranking reads.
 *
 * It is a small panel on purpose. Availability changes weekly; a media kit
 * changes twice a year. Burying a fast-moving flag inside a slow-moving editor
 * is exactly how it ends up permanently, quietly wrong — so this saves on its
 * own and does not share a button with anything else.
 */

const STATUS_TONE = {
    '': 'bg-white text-slate-500 border-slate-300 ring-slate-400',
    open: 'bg-emerald-50 text-emerald-700 border-emerald-300 ring-emerald-500',
    limited: 'bg-amber-50 text-amber-700 border-amber-300 ring-amber-500',
    busy: 'bg-orange-50 text-orange-700 border-orange-300 ring-orange-500',
    closed: 'bg-slate-100 text-slate-500 border-slate-300 ring-slate-400',
};

// The empty one first, and it is the default: a creator who has never opened
// this panel has not told anyone they are taking work, and the profile should
// not say so on their behalf.
const STATUSES = ['', 'open', 'limited', 'busy', 'closed'];
// `availability.status_` + '' is not a key. "not stated" needs a name of its own.
const statusKey = (status) => status || 'none';
const PLATFORM_OPTIONS = ['instagram', 'tiktok', 'youtube', 'x', 'facebook', 'linkedin'];

/** The chip that travels with the creator's name everywhere else. */
export function AvailabilityChip({ availability, compact = false }) {
    const { t } = useTranslation();
    if (!availability) return null;
    const { status = '', open_to_work: openToWork, available_from: from } = availability;

    // "Looking for work" outranks the status: it is the stronger claim, and a
    // creator who set it wants that to be the thing a brand sees.
    if (openToWork) {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                {t('availability.openToWork')}
            </span>
        );
    }

    // Nothing stated, nothing drawn — anywhere. This is what the empty option
    // in the panel buys: no chip on the profile, the press page, search or an
    // applicant row.
    if (!status) return null;
    if (status === 'open' && compact) return null;   // too weak a claim for a list row

    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border flex-shrink-0 ${STATUS_TONE[status]}`}>
            {t(`availability.status_${status}`)}
            {status === 'busy' && from && !compact && (
                <span className="opacity-70">
                    · {new Date(from).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                </span>
            )}
        </span>
    );
}

/**
 * Is there anything to draw at all?
 *
 * With "not stated" as the default, the chip is usually nothing — and a row
 * that renders nothing still renders its own margin. Callers ask this before
 * opening the container rather than each guessing at the same condition.
 */
export function hasAvailability(availability) {
    if (!availability) return false;
    return Boolean(availability.status || availability.open_to_work || availability.note);
}

function TagField({ values, onChange, placeholder, max, t }) {
    const [draft, setDraft] = useState('');

    const add = () => {
        const value = draft.trim();
        if (!value || values.length >= max) return;
        if (!values.some(v => v.toLowerCase() === value.toLowerCase())) {
            onChange([...values, value]);
        }
        setDraft('');
    };

    return (
        <div>
            <div className="flex gap-2">
                <input className="input py-2 text-sm flex-1" value={draft} placeholder={placeholder}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
                <button type="button" onClick={add} disabled={values.length >= max}
                    className="btn-secondary py-2 px-4 text-sm disabled:opacity-40">
                    {t('search.addBtn')}
                </button>
            </div>
            {values.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {values.map((value, index) => (
                        <button key={index} type="button"
                            onClick={() => onChange(values.filter((_, i) => i !== index))}
                            className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition text-[11px] font-semibold">
                            {value} <span className="font-bold ml-0.5">×</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function AvailabilityPanel({ className = '' }) {
    const { t } = useTranslation();
    const [state, setState] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        api.get('/api/creator/availability/')
            .then(res => { if (alive) setState(res.data); })
            .catch(() => { /* the interceptor surfaces auth errors */ });
        return () => { alive = false; };
    }, []);

    if (!state) return null;
    if (state.account_type !== 'creator') return null;

    const set = (patch) => { setState(prev => ({ ...prev, ...patch })); setSaved(false); };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await api.put('/api/creator/availability/', {
                availability: state.availability,
                available_from: state.available_from || null,
                open_to_work: state.open_to_work,
                availability_note: state.availability_note,
                collab_categories: state.collab_categories,
                preferred_platforms: state.preferred_platforms,
                discoverable: state.discoverable,
            });
            setState(res.data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            setError(t('campaigns.saveError'));
        } finally { setSaving(false); }
    };

    const away = ['busy', 'closed'].includes(state.availability);

    return (
        <section className={`rounded-2xl border border-[#c0c0c0] p-5 bg-[#fafafa] ${className}`}>
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {t('availability.title')}
            </h2>
            <p className="text-xs text-slate-400 mb-5 leading-relaxed">{t('availability.hint')}</p>

            <div className="space-y-5">
                {/* Status */}
                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">{t('availability.statusLabel')}</p>
                    <div className="grid grid-cols-2 gap-2">
                        {STATUSES.map(status => (
                            <button key={statusKey(status)} type="button"
                                onClick={() => set({ availability: status })}
                                className={`rounded-xl border-2 px-3 py-2.5 text-left transition ${status === '' ? 'col-span-2' : ''} ${(state.availability || '') === status
                                    ? `${STATUS_TONE[status]} ring-1`
                                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'}`}>
                                <span className="block text-sm font-bold">
                                    {t(`availability.status_${statusKey(status)}`)}
                                </span>
                                <span className="block text-[10px] opacity-70 leading-tight mt-0.5">
                                    {t(`availability.statusDesc_${statusKey(status)}`)}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* "Back on" only makes sense while away — leaving a stale date
                    on an open profile just confuses a brand, and the server
                    clears it for the same reason. */}
                {away && (
                    <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                            {t('availability.availableFrom')}
                        </p>
                        <p className="text-xs text-slate-400 mb-2">{t('availability.availableFromDesc')}</p>
                        <input type="date" className="input py-2 text-sm"
                            value={state.available_from ? String(state.available_from).slice(0, 10) : ''}
                            onChange={(e) => set({ available_from: e.target.value || null })} />
                    </div>
                )}

                <hr className="border-slate-200" />

                {/* Looking for work */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-700">{t('availability.openToWorkLabel')}</p>
                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                            {t('availability.openToWorkDesc')}
                        </p>
                    </div>
                    <button type="button" onClick={() => set({ open_to_work: !state.open_to_work })}
                        className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${state.open_to_work ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${state.open_to_work ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                </div>

                <div>
                    <input className="input py-2 text-sm" value={state.availability_note || ''}
                        maxLength={255} placeholder={t('availability.notePlaceholder')}
                        onChange={(e) => set({ availability_note: e.target.value })} />
                </div>

                <hr className="border-slate-200" />

                {/* What kind of work */}
                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">
                        {t('availability.categoriesLabel')}
                    </p>
                    <p className="text-xs text-slate-400 mb-2 leading-relaxed">
                        {t('availability.categoriesDesc')}
                    </p>
                    <TagField values={state.collab_categories || []} max={12} t={t}
                        placeholder={t('availability.categoriesPlaceholder')}
                        onChange={(rows) => set({ collab_categories: rows })} />
                </div>

                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">
                        {t('availability.platformsLabel')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {PLATFORM_OPTIONS.map(platform => {
                            const on = (state.preferred_platforms || []).includes(platform);
                            return (
                                <button key={platform} type="button"
                                    onClick={() => set({
                                        preferred_platforms: on
                                            ? state.preferred_platforms.filter(p => p !== platform)
                                            : [...(state.preferred_platforms || []), platform],
                                    })}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize ${on
                                        ? 'bg-primary-600 text-white border-primary-600'
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                                    {platform}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <hr className="border-slate-200" />

                {/* Opting out of recommendations without hiding the profile —
                    two different things, so two different switches. */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-700">{t('availability.discoverableLabel')}</p>
                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                            {t('availability.discoverableDesc')}
                        </p>
                    </div>
                    <button type="button" onClick={() => set({ discoverable: !state.discoverable })}
                        className={`w-11 h-6 rounded-full flex items-center transition px-0.5 flex-shrink-0 ${state.discoverable ? 'bg-primary-600' : 'bg-slate-200'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${state.discoverable ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                </div>

                {error && <p className="text-xs text-red-600">{error}</p>}

                <button onClick={save} disabled={saving}
                    className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2">
                    {saving && <span className="spinner-sm" />}
                    {saved ? `✓ ${t('availability.saved')}` : t('availability.save')}
                </button>
            </div>
        </section>
    );
}

export default AvailabilityPanel;
